/**
 * useOverrideManagement — Override check/save for archive content editing
 *
 * Extracted from MainWorkspace to reduce component size.
 * Manages: detecting existing overrides, saving new overrides
 * (Electron local via IPC or cloud via API).
 *
 * @module @humanizer/studio/hooks/useOverrideManagement
 */

import { useState, useCallback, useEffect } from 'react';
import type { ContentItem } from '../contexts/BufferSyncContext';
import type { WorkspaceContent } from '../components/workspace/workspace-types';
import { createLogger } from '../utils/debug';

const log = createLogger('Workspace');

export interface UseOverrideManagementOptions {
  /** Whether thread mode is active */
  isThreadMode: boolean;
  /** Current message in thread mode */
  currentMessage: ContentItem | null;
  /** Display text (for override original) */
  displayText: string;
  /** Active content item */
  content: WorkspaceContent | null | undefined;
  /** Edit content text (for override replacement) */
  editContent: string;
  /** API overrides service */
  api: { overrides: { get: (nodeId: string) => Promise<{ override: unknown }>; save: (nodeId: string, original: string, replacement: string) => Promise<unknown> } };
  /** Whether archive is local (Electron) */
  isLocalArchive: boolean;
  /** Toast notifications */
  addToast: (message: string, type: 'success' | 'info' | 'warning') => void;
  /** View mode setter (to switch back to read after save) */
  setViewMode: (mode: 'read') => void;
}

export interface UseOverrideManagementReturn {
  hasOverride: boolean;
  setHasOverride: (v: boolean) => void;
  isSavingOverride: boolean;
  currentNodeId: string | undefined;
  handleSaveOverride: () => Promise<void>;
}

export function useOverrideManagement({
  isThreadMode,
  currentMessage,
  displayText,
  content,
  editContent,
  api,
  isLocalArchive,
  addToast,
  setViewMode,
}: UseOverrideManagementOptions): UseOverrideManagementReturn {
  const [hasOverride, setHasOverride] = useState(false);
  const [isSavingOverride, setIsSavingOverride] = useState(false);

  const currentNodeId = isThreadMode && currentMessage
    ? currentMessage.metadata.source?.nodeId
    : content?.source?.path?.[1]; // nodeId is second path element

  // Check for existing override when content/message changes
  useEffect(() => {
    if (!currentNodeId) {
      setHasOverride(false);
      return;
    }
    if (isLocalArchive && window.electronOverride) {
      window.electronOverride.get(currentNodeId)
        .then((override) => setHasOverride(override !== null))
        .catch(() => setHasOverride(false));
      return;
    }
    if (!isLocalArchive) {
      api.overrides.get(currentNodeId)
        .then(({ override }) => setHasOverride(override !== null))
        .catch(() => setHasOverride(false));
      return;
    }
    setHasOverride(false);
  }, [currentNodeId, api.overrides, isLocalArchive]);

  const handleSaveOverride = useCallback(async () => {
    if (!currentNodeId) return;
    setIsSavingOverride(true);
    try {
      if (isLocalArchive && window.electronOverride) {
        await window.electronOverride.save(currentNodeId, displayText, editContent);
      } else if (!isLocalArchive) {
        await api.overrides.save(currentNodeId, displayText, editContent);
      } else {
        return;
      }
      setHasOverride(true);
      setViewMode('read');
    } catch (error) {
      log.error('Failed to save override:', error);
      addToast('Failed to save override', 'warning');
    } finally {
      setIsSavingOverride(false);
    }
  }, [currentNodeId, displayText, editContent, api.overrides, isLocalArchive, addToast, setViewMode]);

  return {
    hasOverride,
    setHasOverride,
    isSavingOverride,
    currentNodeId,
    handleSaveOverride,
  };
}
